import React from 'react';
import '../styles/Experience.css';

interface ExperienceItemProps {
  title: string;
  company: string;
  period: string;
  description: string;
  achievements: string[];
  technologies?: string[];
}

const ExperienceItem: React.FC<ExperienceItemProps> = ({
  title,
  company,
  period,
  description,
  achievements,
  technologies
}) => {
  return (
    <div className="experience-item">
      <div className="experience-timeline">
        <div className="timeline-dot"></div>
        <div className="timeline-line"></div>
      </div>
      <div className="experience-content">
        <div className="experience-header">
          <h3>{title}</h3>
          <span className="company">{company}</span>
          <span className="period">{period}</span>
        </div>
        <p className="description">{description}</p>
        <h4>Key Achievements:</h4>
        <ul className="achievements">
          {achievements.map((achievement, index) => (
            <li key={index}>{achievement}</li>
          ))}
        </ul>
        {technologies && (
          <div className="technologies">
            {technologies.map((tech, index) => (
              <span key={index} className="tech-tag">{tech}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Experience: React.FC = () => {
  const experienceData: ExperienceItemProps[] = [
    {
      title: "Web Endpoints Internal Red Team - Locked Shields 2025",
      company: "NATO CCDCOE (Partners Run)",
      period: "Feb 2025 - Mar 2025",
      description: "Participated in NATO's Locked Shields 2025 live-fire cyber defence exercise as part of the Web Endpoints Team, focusing on web application penetration and remediation.",
      achievements: [
        "Conducted live adversarial testing and exploitation on active web services",
        "Identified and exploited vulnerabilities in real-time during high-pressure simulations",
        "Performed patching and hardening of compromised systems post-exploitation",
        "Improved the overall security posture of target systems through strategic remediation",
        "Contributed to Team 06's strong technical rankings, including resilience and situational awareness"
      ],
      technologies: ["Linux", "Burp Suite", "Python", "Web Exploitation", "Patch Management", "Recon Tools"]
    },
    {
      title: "Security Engineer Intern - Internal Systems Security",
      company: "Canva",
      period: "Dec 2024 - Feb 2025",
      description: "Worked within the Internal Systems Security team to enhance endpoint update testing, data analysis, and access control enforcement.",
      achievements: [
        "Developed a robust testing program for endpoint updates, streamlining organisational procedures",
        "Used Snowflake and Mode to extract and visualise data insights",
        "Utilised Open Policy Agent (OPA) to manage access controls and permissions",
        "Worked on an internal HAR Sanitiser tool using Flask",
        "Wrote test cases for both legacy and newly developed code"
      ],
      technologies: ["Flask", "Python", "OPA", "Mode", "Git", "Terraform", "SQL"]
    },
    {
      title: "Workplace Productivity / Junior System Admin Intern",
      company: "Atlassian",
      period: "Nov 2023 - Feb 2024",
      description: "Supported technical operations and infrastructure at Atlassian events and internal systems.",
      achievements: [
        "Provided on-site support for events, including AV setup and Zoom room troubleshooting",
        "Managed user onboarding/offboarding and hardware shipments/reclaims",
        "Troubleshot Windows Server, Linux, and Unix OS issues",
        "Handled device replacements and user access management"
      ],
      technologies: ["Windows Server", "Linux", "Unix", "Zoom", "AV Systems", "Service Desk Tools"]
    },
    {
      title: "Technical Support Officer",
      company: "SHORE School",
      period: "Dec 2022 - Feb 2025",
      description: "Provided technical support and service desk assistance to staff in an educational environment.",
      achievements: [
        "Diagnosed and resolved hardware/software issues on Windows and Mac devices",
        "Conducted device handovers and setups for staff",
        "Supported users across Microsoft environments",
        "Handled physical asset management and logistics"
      ],
      technologies: ["Windows", "macOS", "Microsoft Office", "Service Desk Tools"]
    },
    {
      title: "Junior Developer and Business Analyst",
      company: "Advice RegTech",
      period: "Feb 2022 - Sep 2022",
      description: "Contributed to software testing, development, and business analysis for regulatory technology products.",
      achievements: [
        "Supported configuration and testing of software features using GitHub",
        "Developed basic code and database edits under senior guidance",
        "Designed front-end login systems using Miro",
        "Recognised for reliability and collaborative work ethic"
      ],
      technologies: ["GitHub", "Miro", "SQL", "Python", "Ruby on Rails"]
    },
  ];


  return (
    <section className="experience-section" id="experience">
      <div className="experience-container">
        <h2 className="section-title">Work Experience</h2>
        <p className="section-description">
          My professional journey in security engineering and software development spans
          over 3 years, with expertise in building secure, resilient systems.
        </p>

        <div className="experience-timeline-container">
          {experienceData.map((item, index) => (
            <ExperienceItem key={index} {...item} />
          ))}
          <div className="timeline-end">
            <div className="timeline-dot"></div>
            <span>Career Start</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience; 